/**
 * Study Flashcards Page
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudyLayout } from '../../components/study/StudyLayout';
import { Container } from '../../components/ui/Container';
import { FlashcardCard } from '../../components/study/FlashcardCard';
import { FlashcardGenerator } from '../../components/study/FlashcardGenerator';
import { Button } from '../../components/ui/Button';
import { api } from '../../config/firebase';
import { useStudyStore } from '../../stores/studyStore';
import { ROUTES } from '../../config/constants';
import { toast } from '../../components/ui/Toast';
import { Flashcard } from '@voxapilot/shared';

export function StudyFlashcardsPage() {
  const { documentId, materialId: materialIdParam } = useParams<{ documentId?: string; materialId?: string }>();
  const navigate = useNavigate();
  const { currentDocument, setCurrentDocument, documents, setDocuments } = useStudyStore();
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [currentMaterialId, setCurrentMaterialId] = useState<string | null>(materialIdParam || null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [_isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);

  useEffect(() => {
    // Load documents if not already loaded
    if (documents.length === 0) {
      loadDocuments();
    }
  }, []);

  useEffect(() => {
    if (documentId && !currentDocument) {
      loadDocument();
    }
  }, [documentId]);

  useEffect(() => {
    // Load flashcards if materialId is available
    if (currentMaterialId && flashcards.length === 0) {
      loadFlashcards();
    }
  }, [currentMaterialId]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await api.study.listDocuments();
      if (response.success && response.data) {
        setDocuments(response.data);
      }
    } catch (err: any) {
      console.error('Load documents error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocument = async () => {
    if (!documentId) return;

    try {
      setIsLoading(true);
      const response = await api.study.getDocument(documentId);
      if (response.success && response.data) {
        setCurrentDocument(response.data);
      }
    } catch (err: any) {
      console.error('Load document error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFlashcards = async () => {
    if (!currentMaterialId) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.study.getFlashcards(currentMaterialId);
      if (response.success && response.data) {
        setFlashcards(response.data as Flashcard[]);
        setCurrentIndex(0);
      } else {
        throw new Error(response.error?.message || 'Failed to load flashcards');
      }
    } catch (err: any) {
      console.error('Load flashcards error:', err);
      setError(err.message || 'Failed to load flashcards');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDocumentSelect = (doc: typeof documents[0]) => {
    setCurrentDocument(doc);
    setShowDocumentSelector(false);
    setShowGenerator(true);
  };

  const handleGenerateClick = () => {
    if (!currentDocument) {
      // Show document selector if no document is selected
      if (documents.length > 0) {
        setShowDocumentSelector(true);
      } else {
        // No documents available, navigate to documents page
        navigate(ROUTES.STUDY_DOCUMENTS);
        toast.info('Please upload a document first');
      }
    } else {
      setShowGenerator(true);
    }
  };

  const handleGenerate = async (options: {
    count: number;
    topics?: string[];
    difficulty: 'easy' | 'medium' | 'hard';
  }) => {
    if (!currentDocument) {
      toast.error('No document selected');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      const response = await api.study.generateFlashcards(currentDocument.id, options);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to generate flashcards');
      }

      if (response.data?.flashcards && response.data?.materialId) {
        setFlashcards(response.data.flashcards);
        setCurrentMaterialId(response.data.materialId);
        setCurrentIndex(0);
        setShowGenerator(false);
        toast.success(`Generated ${response.data.flashcards.length} flashcards!`);
      }
    } catch (err: any) {
      console.error('Generate error:', err);
      setError(err.message || 'Failed to generate flashcards');
      toast.error(err.message || 'Failed to generate flashcards');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleMarkKnown = async () => {
    if (!currentMaterialId || flashcards.length === 0) {
      handleNext();
      return;
    }

    const currentFlashcard = flashcards[currentIndex];
    if (!currentFlashcard) {
      handleNext();
      return;
    }

    try {
      await api.study.updateFlashcardReview(currentMaterialId, currentFlashcard.id, true);
      // Update local flashcard state
      setFlashcards((prev) =>
        prev.map((fc) =>
          fc.id === currentFlashcard.id
            ? {
              ...fc,
              reviewCount: (fc.reviewCount || 0) + 1,
              correctCount: (fc.correctCount || 0) + 1,
              lastReviewed: new Date().toISOString(),
            }
            : fc
        )
      );
    } catch (err: any) {
      console.error('Update flashcard review error:', err);
      // Continue anyway - don't block user flow
    }

    handleNext();
  };

  const handleMarkUnknown = async () => {
    if (!currentMaterialId || flashcards.length === 0) {
      handleNext();
      return;
    }

    const currentFlashcard = flashcards[currentIndex];
    if (!currentFlashcard) {
      handleNext();
      return;
    }

    try {
      await api.study.updateFlashcardReview(currentMaterialId, currentFlashcard.id, false);
      // Update local flashcard state
      setFlashcards((prev) =>
        prev.map((fc) =>
          fc.id === currentFlashcard.id
            ? {
              ...fc,
              reviewCount: (fc.reviewCount || 0) + 1,
              lastReviewed: new Date().toISOString(),
            }
            : fc
        )
      );
    } catch (err: any) {
      console.error('Update flashcard review error:', err);
      // Continue anyway - don't block user flow
    }

    handleNext();
  };

  return (
    <StudyLayout>
      <div className="p-6">
        <Container>
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
                Flashcards
              </h1>
              <p className="text-textSecondary dark:text-[#858585]">
                {currentDocument
                  ? `Study flashcards from: ${currentDocument.title}`
                  : 'Generate flashcards from your documents'}
              </p>
            </div>
            {flashcards.length === 0 && (
              <Button onClick={handleGenerateClick} variant="primary">
                Generate Flashcards
              </Button>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Document Selector */}
          {showDocumentSelector && documents.length > 0 && (
            <div className="mb-8 bg-white dark:bg-[#1E1E1E] rounded-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
                Select a Document
              </h2>
              <p className="text-textSecondary dark:text-[#858585] mb-6">
                Choose a document to generate flashcards from:
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {documents
                  .filter((doc) => doc.status === 'completed')
                  .map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => handleDocumentSelect(doc)}
                      className="w-full text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-orange-500 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                    >
                      <h3 className="font-semibold text-textPrimary dark:text-[#CCCCCC] mb-1">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585]">
                        {new Date(doc.createdAt).toLocaleDateString()}
                        {doc.metadata?.topics && doc.metadata.topics.length > 0 && (
                          <span className="ml-2">
                            • {doc.metadata.topics.slice(0, 3).join(', ')}
                          </span>
                        )}
                      </p>
                    </button>
                  ))}
              </div>
              {documents.filter((doc) => doc.status === 'completed').length === 0 && (
                <div className="text-center py-8">
                  <p className="text-textSecondary dark:text-[#858585] mb-4">
                    No analyzed documents available. Please analyze a document first.
                  </p>
                  <Button onClick={() => navigate(ROUTES.STUDY_DOCUMENTS)} variant="primary">
                    Go to Documents
                  </Button>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setShowDocumentSelector(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Generator Modal */}
          {showGenerator && currentDocument && (
            <div className="mb-8">
              <FlashcardGenerator
                documentTitle={currentDocument.title}
                availableTopics={currentDocument.metadata?.topics}
                onGenerate={handleGenerate}
                onCancel={() => {
                  setShowGenerator(false);
                  if (!documentId) {
                    setCurrentDocument(null);
                  }
                }}
                isGenerating={isGenerating}
              />
            </div>
          )}

          {/* Flashcards Display */}
          {flashcards.length > 0 ? (
            <div className="flex flex-col items-center">
              <FlashcardCard
                front={flashcards[currentIndex].front}
                back={flashcards[currentIndex].back}
                index={currentIndex}
                total={flashcards.length}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onMarkKnown={handleMarkKnown}
                onMarkUnknown={handleMarkUnknown}
              />

              {/* Generate More Button */}
              {currentDocument && (
                <div className="mt-8">
                  <Button
                    onClick={() => {
                      setShowGenerator(true);
                      setFlashcards([]);
                      setCurrentMaterialId(null);
                      setCurrentIndex(0);
                    }}
                    variant="outline"
                  >
                    Generate New Set
                  </Button>
                </div>
              )}
            </div>
          ) : !showGenerator && !showDocumentSelector ? (
            <div className="bg-backgroundSecondary dark:bg-[#252526] rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-gray-400 dark:text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-2">
                No flashcards yet
              </h2>
              <p className="text-textSecondary dark:text-[#858585] mb-6">
                {currentDocument
                  ? 'Generate flashcards from your document to start studying'
                  : 'Select a document and generate flashcards to start studying'}
              </p>
              <Button onClick={handleGenerateClick} variant="primary">
                Generate Flashcards
              </Button>
            </div>
          ) : null}
        </Container>
      </div>
    </StudyLayout>
  );
}
